if (!customElements.get('tab-list')) {
  customElements.define(
    'tab-list',
    class TabList extends HTMLUListElement {
      constructor() {
        super();

        this.controls.forEach((button) => button.addEventListener('click', this.handleButtonClick.bind(this)));
      }

      get controls() {
        return this._controls = this._controls || Array.from(this.querySelectorAll('[aria-controls]'));
      }

      handleButtonClick(event) {
        event.preventDefault();

        this.controls.forEach((button) => {
          button.setAttribute('aria-expanded', 'false');

          const panel = document.getElementById(button.getAttribute('aria-controls'));
          panel?.removeAttribute('open');
        });

        const target = event.currentTarget;
        target.setAttribute('aria-expanded', 'true');

        const panel = document.getElementById(target.getAttribute('aria-controls'));
        panel?.setAttribute('open', '');
      }

      reset() {
        const firstControl = this.controls[0];
        firstControl.dispatchEvent(new Event('click'));
      }
    }, { extends: 'ul' }
  );
}

if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    class CartDrawer extends DrawerElement {
      constructor() {
        super();

        this.onPrepareBundledSectionsListener = this.onPrepareBundledSections.bind(this);
        this.onCartRefreshListener = this.onCartRefresh.bind(this);
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      get shouldAppendToBody() {
        return false;
      }

      get recentlyViewed() {
        return this.querySelector('recently-viewed');
      }

      get tabList() {
        return this.querySelector('[is="tab-list"]');
      }

      connectedCallback() {
        super.connectedCallback();

        document.addEventListener('cart:bundled-sections', this.onPrepareBundledSectionsListener);
        document.addEventListener('cart:refresh', this.onCartRefreshListener);
        if (this.recentlyViewed) {
          this.recentlyViewed.addEventListener('is-empty', this.onRecentlyViewedEmpty.bind(this));
        }
      }

      disconnectedCallback() {
        super.disconnectedCallback();
    
        document.removeEventListener('cart:bundled-sections', this.onPrepareBundledSectionsListener);
        document.removeEventListener('cart:refresh', this.onCartRefreshListener);
      }

      onPrepareBundledSections(event) {
        event.detail.sections.push(this.sectionId);
      }

      onRecentlyViewedEmpty() {
        this.recentlyViewed.innerHTML = `
        <div class="drawer__scrollable relative flex justify-center items-start grow shrink text-center">
          <div class="drawer__empty grid gap-5 md:gap-8">
            <p class="drawer__empty-text h2 heading leading-none tracking-tight">${theme.strings.recentlyViewedEmpty}</p>
          </div>
        </div>
        `;
      }

      async onCartRefresh(event) {
        const id = `MiniCart-${this.sectionId}`;
        if (document.getElementById(id) === null) return;

        const responseText = await (await fetch(`${theme.routes.root_url}?section_id=${this.sectionId}`)).text();
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

        document.getElementById(id).innerHTML = parsedHTML.getElementById(id).innerHTML;

        if (event.detail.open === true) {
          this.show();
        }
      }

      show(focusElement = null, animate = true) {
        super.show(focusElement, animate);

        if (this.tabList) {
          this.tabList.reset();

          if (this.open) {
            theme.a11y.trapFocus(this, this.focusElement);
          }
        }
      }
    }
  );
}

if (!customElements.get('cart-remove-button')) {
  customElements.define(
    'cart-remove-button',
    class CartRemoveButton extends HTMLAnchorElement {
      constructor() {
        super();

        this.addEventListener('click', (event) => {
          event.preventDefault();

          const cartItems = this.closest('cart-items');
          cartItems.updateQuantity(this.getAttribute('data-index'), 0);
        });
      }
    }, { extends: 'a' }
  );
}

if (!customElements.get('cart-items')) {
  customElements.define(
    'cart-items',
    class CartItems extends HTMLElement {
      cartUpdateUnsubscriber = undefined;

      constructor() {
        super();

        this.addEventListener('change', theme.utils.debounce(this.onChange.bind(this), 300));
        this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      onChange(event) {
        this.validateQuantity(event);
      }

      onCartUpdate(event) {
        if (event.cart.errors) {
          this.onCartError(event.cart.errors, event.target);
          return;
        }

        if (typeof event.cart.sections === 'undefined') return;
        const sectionToRender = new DOMParser().parseFromString(event.cart.sections[this.sectionId], 'text/html');

        const miniCart = document.querySelector(`#MiniCart-${this.sectionId}`);
        if (miniCart) {
          const updatedElement = sectionToRender.querySelector(`#MiniCart-${this.sectionId}`);
          if (updatedElement) {
            if (event.source === 'cart-discount') {
              const cartDiscount = updatedElement.querySelector(`#CartDiscount-${this.sectionId}`);
              if (cartDiscount) {
                cartDiscount.hidden = false
                cartDiscount.setAttribute('open', 'immediate');
                cartDiscount.setAttribute('active', '');
              }
            }
            miniCart.innerHTML = updatedElement.innerHTML;
          }
        }

        const mainCart = document.querySelector(`#MainCart-${this.sectionId}`);
        if (mainCart) {
          const updatedElement = sectionToRender.querySelector(`#MainCart-${this.sectionId}`);
          if (updatedElement) {
            if (event.source === 'cart-discount') {
              const cartDiscount = updatedElement.querySelector(`#CartDiscount-${this.sectionId}`);
              if (cartDiscount) {
                cartDiscount.hidden = false
                cartDiscount.setAttribute('open', '');
                cartDiscount.setAttribute('aria-expanded', 'true');
              }
            }
            
            const scrollTop = window.scrollY;
            mainCart.innerHTML = updatedElement.innerHTML;

            if (event.source === 'cart-discount') {
              requestIdleCallback(() => {
                window.scrollTo({
                  top: scrollTop,
                  behavior: 'instant'
                });
              });
              
            }
          }
          else {
            mainCart.closest('.cart').classList.add('is-empty');
            mainCart.remove();
          }
        }

        const lineItem = document.getElementById(`CartItem-${event.line}`) || document.getElementById(`CartDrawer-Item-${event.line}`);
        if (lineItem && lineItem.querySelector(`[name="${event.name}"]`)) {
          theme.a11y.trapFocus(mainCart || miniCart, lineItem.querySelector(`[name="${event.name}"]`));
        }
        else if (event.cart.item_count === 0) {
          miniCart
            ? theme.a11y.trapFocus(miniCart, miniCart.querySelector('a'))
            : theme.a11y.trapFocus(document.querySelector('.empty-state'), document.querySelector('.empty-state__link'));
        }
        else {
          miniCart
            ? theme.a11y.trapFocus(miniCart, miniCart.querySelector('.horizontal-product__title'))
            : theme.a11y.trapFocus(mainCart, mainCart.querySelector('.cart__item-title'));
        }

        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: {
            cart: event.cart
          }
        }));
      }

      onCartError(errors, target) {
        if (target) {
          // this.updateQuantity(target.getAttribute('data-index'), target.defaultValue, document.activeElement.getAttribute('name'), target);
          this.disableLoading(target.getAttribute('data-index'));
          this.setValidity(target, errors);
          return;
        }
        else {
          window.location.href = theme.routes.cart_url;
        }

        alert(errors);
      }

      updateQuantity(line, quantity, name, target) {
        this.enableLoading(line);

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

        const body = JSON.stringify({
          id: line,
          quantity,
          sections: sectionsToBundle
        });

        fetch(theme.routes.cart_change_url, { ...theme.utils.fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((parsedState) => {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cart: parsedState, target, line, name });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          });
      }

      enableLoading(line) {
        this.classList.add('pointer-events-none');

        const loader = document.getElementById(`Loader-${this.sectionId}-${line}`);
        if (loader) loader.hidden = false;
      }

      disableLoading(line) {
        this.classList.remove('pointer-events-none');

        const loader = document.getElementById(`Loader-${this.sectionId}-${line}`);
        if (loader) loader.hidden = true;
      }

      setValidity(target, message) {
        target.setCustomValidity(message);
        target.reportValidity();
        target.value = target.defaultValue;
        target.select();
      }

      validateQuantity(event) {
        const target = event.target;
        const inputValue = parseInt(target.value);
        const index = target.getAttribute('data-index');
        let message = '';

        if (inputValue < parseInt(target.getAttribute('data-min'))) {
          message = theme.quickOrderListStrings.minError.replace('[min]', target.getAttribute('data-min'));
        }
        else if (inputValue > parseInt(target.max)) {
          message = theme.quickOrderListStrings.maxError.replace('[max]', target.max);
        }
        else if (inputValue % parseInt(target.step) !== 0) {
          message = theme.quickOrderListStrings.stepError.replace('[step]', target.step);
        }

        if (message) {
          this.setValidity(target, message);
        }
        else {
          target.setCustomValidity('');
          target.reportValidity();
          this.updateQuantity(index, inputValue, document.activeElement.getAttribute('name'), target);
        }
      }
    }
  );
}

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener('change', theme.utils.debounce(this.onChange.bind(this), 300));
      }

      onChange(event) {
        const body = JSON.stringify({ note: event.target.value });
        fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig(), ...{ body } });
      }
    }
  );
}

if (!customElements.get('main-cart')) {
  customElements.define(
    'main-cart',
    class MainCart extends HTMLElement {
      constructor() {
        super();

        document.addEventListener('cart:bundled-sections', this.onPrepareBundledSections.bind(this));
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      onPrepareBundledSections(event) {
        event.detail.sections.push(this.sectionId);
      }
    }
  );
}

if (!customElements.get('country-province')) {
  customElements.define(
    'country-province',
    class CountryProvince extends HTMLElement {
      constructor() {
        super();

        this.provinceElement = this.querySelector('[name="address[province]"]');
        this.countryElement = this.querySelector('[name="address[country]"]');
        this.countryElement.addEventListener('change', this.handleCountryChange.bind(this));

        if (this.getAttribute('country') !== '') {
          this.countryElement.selectedIndex = Math.max(0, Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.getAttribute('data-country')));
          this.countryElement.dispatchEvent(new Event('change'));
        }
        else {
          this.handleCountryChange();
        }
      }

      handleCountryChange() {
        const option = this.countryElement.options[this.countryElement.selectedIndex], provinces = JSON.parse(option.getAttribute('data-provinces'));
        this.provinceElement.parentElement.hidden = provinces.length === 0;

        if (provinces.length === 0) {
          return;
        }

        this.provinceElement.innerHTML = '';

        provinces.forEach((data) => {
          const selected = data[1] === this.getAttribute('data-province');
          this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
        });
      }
    }
  );
}

/**
 * Shipping Calculator (CEP-only) — usa ViaCEP para resolver UF/cidade
 * e consulta /cart/shipping_rates.json sem pedir país/estado ao usuário.
 *
 * Requisitos de HTML:
 *  - Deve ser um <form is="shipping-calculator"> ... </form>
 *  - Precisa ter um input: name="address[zip]"
 *  - Precisa ter um botão: type="submit"
 *  - O último elemento do form deve ser o container de resultados (como no seu código)
 *
 * Obs:
 *  - NÃO precisa ter campos de país/estado no form.
 *  - Funciona no carrinho (carrinho real). No PDP, ele calcula em cima do carrinho atual
 *    (sem simular add.js). Se quiser simular PDP depois, eu habilito via flag.
 */
if (!customElements.get('shipping-calculator')) {
  customElements.define(
    'shipping-calculator',
    class ShippingCalculator extends HTMLFormElement {
      constructor() {
        super();
        this.onSubmitHandler = this.onSubmit.bind(this);
        this.onZipInputHandler = this.onZipInput.bind(this);
        this.onZipKeydownHandler = this.onZipKeydown.bind(this);
        this.abortController = null;
      }

      connectedCallback() {
        this.submitButton = this.querySelector('[type="submit"]');
        this.resultsElement = this.lastElementChild;
        this.zipInput = this.querySelector('[name="address[zip]"]');

        if (!this.submitButton || !this.resultsElement || !this.zipInput) return;

        /* ✅ CEP-only: remove validações e campos de país/província */
        this.setAttribute('novalidate', 'novalidate');

        const hideFieldBlock = (fieldEl) => {
          if (!fieldEl) return;
          const block =
            fieldEl.closest('.field') ||
            fieldEl.closest('.form__field') ||
            fieldEl.closest('.field__input-wrapper') ||
            fieldEl.closest('.select') ||
            fieldEl.closest('.form-row') ||
            fieldEl.closest('.grid__item') ||
            fieldEl.closest('div') ||
            fieldEl.parentElement;

          if (block) block.style.display = 'none';
        };

        const countryEl = this.querySelector('[name="address[country]"], [name="address[country_code]"], [name="address[country_id]"]');
        const provinceEl = this.querySelector('[name="address[province]"], [name="address[province_code]"]');

        [countryEl, provinceEl].forEach((el) => {
          if (!el) return;
          el.removeAttribute('required');
          el.disabled = true;
          el.tabIndex = -1;
          el.setAttribute('aria-hidden', 'true');
          hideFieldBlock(el); // ✅ esconde label + wrapper inteiro
        });

        /* ✅ Input CEP: limita, mascara e melhora UX */
        this.zipInput.setAttribute('inputmode', 'numeric');
        this.zipInput.setAttribute('autocomplete', 'postal-code');
        this.zipInput.setAttribute('maxlength', '9'); // 99999-999 = 9 chars
        this.zipInput.setAttribute('pattern', '\\d{5}-\\d{3}');

        // Se VMasker existir, usa (ok)
        if (window.VMasker) {
          window.VMasker(this.zipInput).maskPattern('99999-999');
        }

        // Fallback: máscara nativa via JS (sempre ativa, mesmo sem VMasker)
        this.zipInput.addEventListener('input', this.onZipInputHandler);
        this.zipInput.addEventListener('keydown', this.onZipKeydownHandler);

        // Preenche CEP salvo
        try {
          const saved = localStorage.getItem('cepCache');
          if (saved) this.zipInput.value = this.formatCep(saved);
        } catch (_) {}

        this.submitButton.addEventListener('click', this.onSubmitHandler);
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.submitButton?.removeEventListener('click', this.onSubmitHandler);
        this.zipInput?.removeEventListener('input', this.onZipInputHandler);
        this.zipInput?.removeEventListener('keydown', this.onZipKeydownHandler);
      }

      // ---------------- CEP Mask / Limit ----------------
      digitsOnly(str) {
        return (str || '').replace(/\D/g, '');
      }

      formatCep(digits) {
        const s = this.digitsOnly(digits).slice(0, 8);
        if (s.length <= 5) return s;
        return `${s.slice(0, 5)}-${s.slice(5)}`;
      }

      onZipInput() {
        // mantém cursor decente e força máscara + limite
        const before = this.zipInput.value;
        const masked = this.formatCep(before);
        if (before !== masked) this.zipInput.value = masked;
      }

      onZipKeydown(e) {
        // bloqueia letras e símbolos (mas permite navegação/atalhos)
        const allowed =
          e.key === 'Backspace' ||
          e.key === 'Delete' ||
          e.key === 'ArrowLeft' ||
          e.key === 'ArrowRight' ||
          e.key === 'Tab' ||
          e.key === 'Home' ||
          e.key === 'End' ||
          e.ctrlKey || e.metaKey;

        if (allowed) return;

        // permite só números
        if (!/^\d$/.test(e.key)) {
          e.preventDefault();
          return;
        }

        // limita a 8 dígitos (independente do hífen)
        const digits = this.digitsOnly(this.zipInput.value);
        if (digits.length >= 8) {
          e.preventDefault();
        }
      }

      zipDigits() {
        return this.digitsOnly(this.zipInput.value).slice(0, 8);
      }

      // ---------------- Helpers ----------------
      getUFName(uf) {
        const map = {
          AC:"Acre", AL:"Alagoas", AP:"Amapá", AM:"Amazonas", BA:"Bahia", CE:"Ceará", DF:"Distrito Federal",
          ES:"Espírito Santo", GO:"Goiás", MA:"Maranhão", MT:"Mato Grosso", MS:"Mato Grosso do Sul", MG:"Minas Gerais",
          PA:"Pará", PB:"Paraíba", PR:"Paraná", PE:"Pernambuco", PI:"Piauí", RJ:"Rio de Janeiro", RN:"Rio Grande do Norte",
          RS:"Rio Grande do Sul", RO:"Rondônia", RR:"Roraima", SC:"Santa Catarina", SP:"São Paulo", SE:"Sergipe", TO:"Tocantins"
        };
        return map[uf] || uf;
      }

      fetchConfig() {
        const cfg =
          (window.theme && theme.utils && typeof theme.utils.fetchConfig === 'function')
            ? theme.utils.fetchConfig('javascript')
            : { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'xmlhttprequest' }, credentials: 'same-origin' };

        cfg.headers = cfg.headers || {};
        cfg.headers['Content-Type'] = cfg.headers['Content-Type'] || 'application/json';
        cfg.headers['X-Requested-With'] = cfg.headers['X-Requested-With'] || 'xmlhttprequest';
        cfg.credentials = cfg.credentials || 'same-origin';
        cfg.method = 'POST';
        return cfg;
      }

      cartRatesUrl() {
        let url = '/cart/shipping_rates.json';
        if (window.theme && theme.routes && theme.routes.cart_url) {
          url = `${theme.routes.cart_url}/shipping_rates.json`;
        }
        return url.replace('//', '/');
      }

      async viaCep(zipDigits) {
        const r = await fetch(`https://viacep.com.br/ws/${zipDigits}/json/`);
        const j = await r.json();
        if (j.erro) throw new Error('Por favor, insira um CEP válido.');
        return j;
      }

      setBusy(isBusy) {
        if (!this.submitButton) return;
        if (isBusy) this.submitButton.setAttribute('aria-busy', 'true');
        else this.submitButton.removeAttribute('aria-busy');
      }

      // ---------------- Main ----------------
      onSubmit(event) {
        event.preventDefault();

        this.abortController?.abort();
        this.abortController = new AbortController();

        const zip = this.zipDigits();
        if (zip.length !== 8) {
          this.formatError({ zip: 'Digite um CEP com 8 números. Ex.: 88888-888' });
          this.resultsElement.hidden = false;
          return;
        }

        try { localStorage.setItem('cepCache', zip); } catch (_) {}

        this.setBusy(true);

        this.viaCep(zip)
          .then((addr) => {
            const province = this.getUFName(addr.uf);
            const city = addr.localidade;

            const body = JSON.stringify({
              shipping_address: {
                country: 'Brazil',
                province,
                city,
                zip
              }
            });

            const url = this.cartRatesUrl();
            const cfg = this.fetchConfig();

            return fetch(url, { ...cfg, body, signal: this.abortController.signal })
              .then((response) => response.json())
              .then((parsedState) => ({ parsedState, city, province }));
          })
          .then(({ parsedState, city, province }) => {
            if (parsedState && parsedState.shipping_rates) {
              this.formatShippingRates(parsedState.shipping_rates, city, province);
            } else {
              this.formatError(parsedState || { error: 'Não foi possível calcular o frete.' });
            }
          })
          .catch((error) => {
            if (error.name === 'AbortError') return;
            console.error(error);
            this.formatError({ error: 'Ocorreu um erro durante o cálculo do frete. Tente novamente.' });
          })
          .finally(() => {
            this.resultsElement.hidden = false;
            this.setBusy(false);
          });
      }

      // ---------------- UI ----------------
      formatError(errors) {
        const list = Object.keys(errors || {}).map((k) => `<li>${errors[k]}</li>`);

        this.resultsElement.classList.remove('alert--success');
        this.resultsElement.classList.add('alert--error');

        const errorTitle =
          (window.theme && theme.shippingCalculatorStrings && theme.shippingCalculatorStrings.error)
            ? theme.shippingCalculatorStrings.error
            : 'Não foi possível calcular o frete.';

        this.resultsElement.lastElementChild.innerHTML = `
          <p>${errorTitle}</p>
          ${list.length ? `<ul class="list-disc grid gap-2" role="list">${list.join('')}</ul>` : ''}
        `;
      }

      formatShippingRates(shippingRates, city, province) {
        const list = (shippingRates || []).map(({ presentment_name, currency, price }) => {
          return `<li>${presentment_name}: ${currency} ${price}</li>`;
        });

        if ((shippingRates || []).length) {
          this.resultsElement.classList.remove('alert--error');
          this.resultsElement.classList.add('alert--success');
        } else {
          this.resultsElement.classList.remove('alert--success');
          this.resultsElement.classList.add('alert--error');
        }

        const strings = (window.theme && theme.shippingCalculatorStrings) ? theme.shippingCalculatorStrings : {};
        const title =
          (shippingRates || []).length === 0 ? (strings.notFound || 'Nenhuma opção encontrada.')
          : (shippingRates || []).length === 1 ? (strings.oneResult || '1 opção encontrada')
          : (strings.multipleResults || 'Opções encontradas');

        const where = `<p style="margin:0 0 .5rem 0; opacity:.8;">Para ${city} - ${province}</p>`;

        this.resultsElement.lastElementChild.innerHTML = `
          ${where}
          <p>${title}</p>
          ${list.length ? `<ul class="list-disc grid gap-2" role="list">${list.join('')}</ul>` : ''}
        `;
      }
    },
    { extends: 'form' }
  );
}
if (!customElements.get('cart-discount')) {
  customElements.define(
    'cart-discount',
    class CartDiscount extends HTMLFormElement {
      constructor() {
        super();

        this.onApplyDiscount = this.applyDiscount.bind(this);
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }
      
      connectedCallback() {
        this.submitButton = this.querySelector('[type="submit"]');
        this.resultsElement = this.lastElementChild;

        this.submitButton.addEventListener('click', this.onApplyDiscount);
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.submitButton.removeEventListener('click', this.onApplyDiscount);
      }

      applyDiscount(event) {
        event.preventDefault();

        const discountCode = this.querySelector('[name="discount"]');
        if (!(discountCode instanceof HTMLInputElement) || typeof this.getAttribute('data-section-id') !== 'string') return;

        this.abortController?.abort();
        this.abortController = new AbortController();

        const discountCodeValue = discountCode.value.trim();
        if (discountCodeValue === '') return;

        const existingDiscounts = this.existingDiscounts();
        if (existingDiscounts.includes(discountCodeValue)) return;

        this.setDiscountError('');
        this.submitButton.setAttribute('aria-busy', 'true');

        const body = JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.sectionId]
        });
        
        fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig('json'), ...{ body }, signal: this.abortController.signal })
          .then((response) => response.json())
          .then((parsedState) => {
            if (
              parsedState.discount_codes.find((discount) => {
                return discount.code === discountCodeValue && discount.applicable === false;
              })
            ) {
              discountCode.value = '';
              this.setDiscountError(theme.discountStrings.error);
              return;
            }

            const newHtml = parsedState.sections[this.sectionId];
            const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
            const section = parsedHtml.getElementById(`shopify-section-${this.sectionId}`);
            if (section) {
              const discountCodes = section?.querySelectorAll('button[is="discount-remove"]') || [];
              const codes = Array.from(discountCodes)
                .map((element) => (element instanceof HTMLButtonElement ? element.getAttribute('data-discount') : null))
                .filter(Boolean);

              if (
                codes.length === existingDiscounts.length &&
                codes.every((code) => existingDiscounts.includes(code)) &&
                parsedState.discount_codes.find((discount) => {
                  return discount.code === discountCodeValue && discount.applicable === true;
                })
              ) {
                discountCode.value = '';
                this.setDiscountError(theme.discountStrings.shippingError);
                return;
              }
            }

            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount', cart: parsedState });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          })
          .finally(() => {
            this.submitButton.removeAttribute('aria-busy');
          });
      }

      removeDiscount(event) {
        if ((event instanceof KeyboardEvent && event.key !== 'Enter') || !(event instanceof MouseEvent)) {
          return;
        }

        const discountCode = event.target.getAttribute('data-discount');
        if (!discountCode) return;

        const existingDiscounts = this.existingDiscounts();
        const index = existingDiscounts.indexOf(discountCode);
        if (index === -1) return;

        existingDiscounts.splice(index, 1);

        this.abortController?.abort();
        this.abortController = new AbortController();

        this.setDiscountError('');
        event.target.setAttribute('loading', '');

        const body = JSON.stringify({
          discount: existingDiscounts.join(','),
          sections: [this.sectionId]
        });
        
        fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig('json'), ...{ body }, signal: this.abortController.signal })
          .then((response) => response.json())
          .then((parsedState) => {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount', cart: parsedState });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          })
          .finally(() => {
            event.target.removeAttribute('loading');
          });
      }

      existingDiscounts() {
        const discountCodes = [];
        const discountPills = this.querySelectorAll('button[is="discount-remove"]');
        for (const pill of discountPills) {
          if (pill.hasAttribute('data-discount')) {
            discountCodes.push(pill.getAttribute('data-discount'));
          }
        }
        return discountCodes;
      }

      setDiscountError(error) {
        this.resultsElement.lastElementChild.textContent = error;
        this.resultsElement.hidden = error.length === 0;
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('discount-remove')) {
  customElements.define(
    'discount-remove',
    class DiscountRemove extends MagnetButton {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick(event) {
        const form = this.closest('form[is="cart-discount"]') || document.querySelector('form[is="cart-discount"]');

        if (form) {
          event.preventDefault();
          form.removeDiscount(event);
        }
      }
    }, { extends: 'button' }
  );
}
